import { Clock, Inbox, CheckCircle2, AlertTriangle, Users } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Badge } from '../ui/badge'
import type { SupplierCounters, SupplierListFilter } from '@/lib/api'

type FilterConfig = {
  key: SupplierListFilter
  label: string
  icon: React.ReactNode
  counterKey: keyof SupplierCounters
}

const FILTERS: FilterConfig[] = [
  { key: 'all', label: 'Tous', icon: <Users className="h-3.5 w-3.5" />, counterKey: 'all' },
  { key: 'pending', label: 'En attente', icon: <Inbox className="h-3.5 w-3.5" />, counterKey: 'pending' },
  { key: 'received', label: 'Reçus', icon: <CheckCircle2 className="h-3.5 w-3.5" />, counterKey: 'received' },
  { key: 'validated', label: 'Validés', icon: <CheckCircle2 className="h-3.5 w-3.5" />, counterKey: 'validated' },
  { key: 'overdue', label: 'En retard', icon: <AlertTriangle className="h-3.5 w-3.5" />, counterKey: 'overdue' },
]

export function SupplierFilters({
  filter,
  counters,
  onFilterChange,
}: {
  filter: SupplierListFilter
  counters: SupplierCounters | null
  onFilterChange: (next: SupplierListFilter) => void
}) {
  return (
    <div
      role="tablist"
      aria-label="Filtres fournisseurs"
      className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1"
    >
      {FILTERS.map((f) => {
        const active = filter === f.key
        const count = counters?.[f.counterKey] ?? null
        return (
          <button
            key={f.key}
            type="button"
            role="tab"
            aria-selected={active}
            aria-label={`Filtre ${f.label}${count !== null ? ` (${count})` : ''}`}
            onClick={() => onFilterChange(f.key)}
            className={cn(
              'inline-flex shrink-0 items-center gap-2 rounded-full border px-3 py-1.5 text-sm font-medium transition-colors',
              'focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
              active
                ? 'border-primary bg-primary/10 text-primary'
                : 'border-transparent bg-muted text-muted-foreground hover:bg-muted/80'
            )}
          >
            {f.key === 'overdue' ? (
              <Clock
                className={cn('h-3.5 w-3.5', active ? 'text-primary' : 'text-muted-foreground')}
              />
            ) : (
              f.icon
            )}
            {f.label}
            {count !== null && (
              <Badge
                variant="secondary"
                className={cn(
                  'ml-1 h-5 min-w-5 justify-center px-1.5 text-xs',
                  active && 'bg-primary/20 text-primary'
                )}
              >
                {count}
              </Badge>
            )}
          </button>
        )
      })}
    </div>
  )
}
